import { createSimpleStore } from "@expert/state-utils";

/** Which off-call detail view the right panel is showing. */
export type HubPanelView = "leaderboard" | "assist" | "transfer-rate" | "today-sales";

interface OffCallPanelState {
    open: boolean;
    view: HubPanelView | null;
    togglePanel: (view: HubPanelView) => void;
    closePanel: () => void;
}

/**
 * Controls the hub's right slide-in panel (metric detail / leaderboard / AI assist).
 *
 * The triggers live deep inside the Expert Hub (leaderboard card, floating assist button) while
 * the consumer — the sibling `HubRightPanel` plus the layout's `rightTopOpen` prop up in the
 * redesigned content components — sits elsewhere in the tree, so a store beats threading callbacks +
 * lifted state through several layers.
 *
 * Off-call the panel starts closed (Expert Hub full-width); it opens to a 70/30 split when a trigger
 * is clicked. `togglePanel` is trigger-driven: clicking the trigger for the view that's already open
 * closes it, while clicking a different trigger switches the panel to that view. `closePanel` (and
 * the close half of a toggle) keeps `view` set so the slide-out animation still renders the last
 * content instead of blanking mid-close.
 *
 * Select scalars individually (`(s) => s.open`, `(s) => s.view`) so unrelated updates don't
 * re-render consumers.
 */
export const useHubPanelStore = createSimpleStore<OffCallPanelState>({
    devtools: {
        store: "hub-right-panel",
        name: "expert-hub/right-panel",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set) => ({
    open: false,
    view: null,
    togglePanel: (view) => set((state) => (state.open && state.view === view ? { open: false } : { open: true, view })),
    closePanel: () => set({ open: false }),
}));
